import React, { useEffect, useState } from "react";
import InputComponent from "../UniversalComponents/InputComponent";
import Button from "../UniversalComponents/DefaultButton";
import { getUserToken } from "../../utils/utilityFunctions/localServices";
import { useGetFinancialDetailsQuery, usePostFinancialDetailsMutation } from "../../utils/customer/customerTaskTable";
import { useDispatch } from "react-redux";
import { pushAlert } from "../../utils/alertSlice";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import BackArrowCompoentIcon from "../../assets/images/icons/BackArrowCompoentIcon";
import { ReactComponent as CircleEdit } from "../../assets/images/icons/CircleEdit.svg";
import { ReactComponent as CircleDelete } from "../../assets/images/icons/CircleDelete.svg";
import { ReactComponent as AddField } from "../../assets/images/icons/AddField.svg";
import { useLocation, useNavigate } from "react-router-dom";

const FinancialDetailsForm = ({ vendorUUID, editData, handleBackButtonClick, vendorName }) => {
  const dispatch = useDispatch();
  const userToken = getUserToken();
  const navigate = useNavigate();
  const location = useLocation();
  const VendorID = location?.state?.vendorData?.id;
  const [financialData, setFinancialData] = useState([]);
  const [formData, setFormData] = useState({
    bank_account_name: "",
    routing_number: "",
    bank_account_number: "",
    payment_terms: "",
    opening_balance: "",
    id: "",
    is_primary: 0,
  });
  const [showAddForm, setShowAddForm] = useState(location.state?.isShowMode ?? false);
  const [selectedEditIndex, setSelectedEditIndex] = useState(null);
  const [defaultIndex, setDefaultIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isDisable, setIsDisable] = useState("");
  const [isLocalUpdate, setIsLocalUpdate] = useState(false); // Track local updates to prevent useEffect overwrite

  const [postFinancialDetails, { isLoading, isSuccess, isError, data: response }] = usePostFinancialDetailsMutation();

  const {
    data: getData,
    isSuccess: isFetched,
    refetch,
  } = useGetFinancialDetailsQuery(
    { UUID: editData?.row_id || VendorID, token: userToken },
    { skip: !editData?.row_id && !VendorID }
  );

  useEffect(() => {
    console.log("useEffect: vendorName received", { vendorName, VendorID, editData, isLocalUpdate, isSuccess });
    if (isFetched && getData?.data?.length > 0 && !isLocalUpdate) {
      console.log("useEffect: Setting financialData from getData", {
        getData: getData.data,
        financialData,
        showAddForm,
      });
      setFinancialData(getData.data);
      const defaultIdx = getData.data.findIndex((f) => f.is_primary === 1);
      setDefaultIndex(defaultIdx !== -1 ? defaultIdx : null);
      if (location.state?.activeTab === "financial" && location.state?.isShowMode && !isSuccess) {
        const primaryIndex = getData.data.findIndex((f) => f.is_primary === 1);
        const index = primaryIndex !== -1 ? primaryIndex : 0;
        setFormData({
          bank_account_name: getData.data[index]?.bank_account_name || "",
          routing_number: getData.data[index]?.routing_number || "",
          bank_account_number: getData.data[index]?.bank_account_number || "",
          payment_terms: getData.data[index]?.payment_terms || "",
          opening_balance: getData.data[index]?.opening_balance || "",
          id: getData.data[index]?.id || "",
          is_primary: getData.data[index]?.is_primary || 0,
        });
        setSelectedEditIndex(index);
        setIsDisable(index.toString());
        setShowAddForm(true);
      } else if (!isSuccess) {
        setShowAddForm(false); // Only set to false if not in success state
        setFormData({
          bank_account_name: "",
          routing_number: "",
          bank_account_number: "",
          payment_terms: "",
          opening_balance: "",
          id: "",
          is_primary: 0,
        });
        setSelectedEditIndex(null);
        setIsDisable("");
      }
    } else if (location.state?.activeTab === "financial" && location.state?.isShowMode && !isSuccess) {
      setShowAddForm(true);
      setFormData({
        bank_account_name: "",
        routing_number: "",
        bank_account_number: "",
        payment_terms: "",
        opening_balance: "",
        id: "",
        is_primary: 0,
      });
      setSelectedEditIndex(null);
      setIsDisable("");
    } else if (!isLocalUpdate && !isSuccess) {
      console.log("useEffect: Resetting financialData to empty", { getData, financialData, showAddForm });
      setFinancialData([]);
      setShowAddForm(true); // Show form if financialData is empty
      setFormData({
        bank_account_name: "",
        routing_number: "",
        bank_account_number: "",
        payment_terms: "",
        opening_balance: "",
        id: "",
        is_primary: 0,
      });
      setSelectedEditIndex(null);
      setIsDisable("");
    }
  }, [isFetched, getData, location.state, isLocalUpdate, isSuccess]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Input changed:", { name, value, formData: { ...formData, [name]: value } });
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Relaxed validation to only require fields marked with asterisk in UI
  const isValid = (data) => {
    const valid =
      data.bank_account_name &&
      data.bank_account_name.trim() !== "" &&
      data.bank_account_number &&
      data.bank_account_number.trim() !== "";
    console.log("isValid check:", { data, valid });
    return valid;
  };

  const handleSaveAndSubmit = () => {
    const { bank_account_name, bank_account_number } = formData;

    console.log("handleSaveAndSubmit: Current formData", { formData, showAddForm });

    if (!bank_account_name || bank_account_name.trim() === "") {
      dispatch(
        pushAlert({
          message: "Bank Account Name is required",
          type: "danger",
          visible: true,
        })
      );
      return;
    }

    if (!bank_account_number || bank_account_number.trim() === "") {
      dispatch(
        pushAlert({
          message: "Account Number is required",
          type: "danger",
          visible: true,
        })
      );
      return;
    }

    let updated;

    if (selectedEditIndex !== null) {
      updated = [...financialData];
      updated[selectedEditIndex] = {
        ...formData,
        id: financialData[selectedEditIndex]?.id,
      };
      console.log("handleSaveAndSubmit: Updating existing record", { updated, selectedEditIndex, showAddForm });
      setFinancialData(updated);
      setSelectedEditIndex(null);
      setShowAddForm(false); // Close form after UPDATE
      resetForm();
      setIsLocalUpdate(true); // Mark as local update to prevent useEffect overwrite
      submitFinancialDetails(updated);
    } else {
      updated = [...financialData, { ...formData, is_primary: financialData.length === 0 ? 1 : 0 }]; // Set is_primary to 1 for first record
      console.log("handleSaveAndSubmit: Adding new record", { updated, formData, showAddForm });
      setFinancialData(updated);
      setShowAddForm(false); // Close form after SAVE
      resetForm();
      setIsLocalUpdate(true); // Mark as local update to prevent useEffect overwrite
      submitFinancialDetails(updated);
    }
  };

  const resetForm = () => {
    console.log("resetForm: Resetting formData", { formData, showAddForm });
    setFormData({
      bank_account_name: "",
      routing_number: "",
      bank_account_number: "",
      payment_terms: "",
      opening_balance: "",
      id: "",
      is_primary: 0,
    });
    setSelectedEditIndex(null);
    setIsDisable("");
  };

  const submitFinancialDetails = async (list) => {
    const cleanedList = list.filter((f) => isValid(f));
    const payload = {
      financials: cleanedList.map((f, i) => ({
        ...f,
        opening_balance: f.opening_balance ? parseFloat(f.opening_balance) : null,
        is_primary: f.is_primary,
        ...(f.id && { id: f.id }),
      })),
    };

    console.log("submitFinancialDetails:", { payload, formData, financialData, cleanedList, showAddForm });

    try {
      const response = await postFinancialDetails({
        UUID: editData?.row_id || vendorUUID || VendorID,
        token: userToken,
        body: payload,
      }).unwrap();
      // Always refetch to update query cache
      refetch();
      // Sync financialData with API response if available
      if (response?.data?.length > 0) {
        console.log("submitFinancialDetails: Syncing financialData with API response", {
          data: response.data,
          showAddForm,
        });
        setFinancialData(response.data);
      }
      setIsLocalUpdate(false); // Allow useEffect to sync with backend after save
      setShowAddForm(false); // Ensure form is closed after API success
      dispatch(
        pushAlert({
          message: "Financial details saved successfully",
          type: "success",
          visible: true,
        })
      );
    } catch (err) {
      console.error("Failed to submit financial details", err);
      dispatch(
        pushAlert({
          message: err?.data?.message || "Failed to save financial details",
          type: "danger",
          visible: true,
        })
      );
      setIsLocalUpdate(false); // Reset to allow useEffect to sync on error
    }
  };

  const handleDefaultChange = async (index) => {
    const updated = financialData.map((item, i) => ({
      ...item,
      is_primary: i === index ? 1 : 0,
    }));
    console.log("handleDefaultChange: Setting default bank", { updated, index, showAddForm });
    setFinancialData(updated);
    setDefaultIndex(index);
    setIsLocalUpdate(true); // Mark as local update
    await submitFinancialDetails(updated);
  };

  const handleEdit = (index) => {
    const newFormData = {
      bank_account_name: financialData[index].bank_account_name || "",
      routing_number: financialData[index].routing_number || "",
      bank_account_number: financialData[index].bank_account_number || "",
      payment_terms: financialData[index].payment_terms || "",
      opening_balance: financialData[index].opening_balance || "",
      id: financialData[index].id || "",
      is_primary: financialData[index].is_primary || 0,
    };
    console.log("handleEdit: Setting formData", {
      index,
      newFormData,
      financialData: financialData[index],
      showAddForm,
    });
    setFormData(newFormData);
    setSelectedEditIndex(index);
    setShowAddForm(true); // Open form for editing
    setIsDisable(index.toString());
  };

  const handleDelete = (index) => {
    const updated = [...financialData];
    updated.splice(index, 1);
    console.log("handleDelete: Deleting record", { updated, index, showAddForm });
    setFinancialData(updated);
    resetForm();
    setShowAddForm(financialData.length > 1 ? false : true); // Show form if no data remains
    setIsLocalUpdate(true); // Mark as local update
  };

  const handleCancelForm = () => {
    console.log("handleCancelForm: Canceling form", { financialData, showAddForm });
    resetForm();
    setShowAddForm(financialData.length > 0 ? false : true); // Show form if no data, else show list
  };

  useEffect(() => {
    if (isLoading) dispatch(showLoader());
    else dispatch(hideLoader());

    if (isSuccess) {
      console.log("useEffect: API success, closing form", { isSuccess, response, showAddForm });
      dispatch(pushAlert({ message: response?.message || "Saved", type: "success", visible: true }));
      setShowAddForm(false); // Close form on API success
    }
    if (isError) {
      console.log("useEffect: API error", { isError, response, showAddForm });
      dispatch(pushAlert({ message: response?.message || "Error", type: "danger", visible: true }));
    }
  }, [isLoading, isSuccess, isError, response, dispatch]);

  return (
    <div className="h-full flex flex-col pl-4">
      <div className="flex flex-col w-full h-full">
        <div className="flex justify-between items-center border-[#D2DAE1] h-fit">
          <div className="flex items-center px-2 py-2">
            <button
              onClick={() => {
                if (location.pathname === "/add-vendor") {
                  navigate("/vendors");
                } else {
                  navigate(`/vendor-profile/${location?.state?.vendorData?.id}`, {
                    state: { isEditMode: true },
                  });
                }
              }}
            >
              <BackArrowCompoentIcon />
            </button>
            <h3 className="text-16 font-600 text-[#383838] mx-2 items-center">
              <span className="items-center text-[rgba(56,56,56,0.6)] text-16 font-500">Vendor &gt; </span>
              {vendorName || "Vendor"} {/* Fallback if vendorName is undefined */}
            </h3>
          </div>
          {!showAddForm && (
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true); // Open blank form
              }}
              className="group cursor-pointer border border-[#D2DAE1] hover:border-[#004EFF] rounded-md flex items-center gap-2 px-3 py-1.5 mr-4"
            >
              <AddField className="w-4 h-4 fill-[#666] group-hover:fill-[#004EFF]" />
              <span className="text-base font-normal text-[#666] group-hover:text-[#004EFF]">Add New</span>
            </button>
          )}
        </div>

        <div className="flex flex-col gap-2 h-full pr-4 mb-4 grow min-h-0">
          {(showAddForm || financialData.length === 0) && (
            <div className="mb-6 border rounded p-4">
              <div className="grid grid-cols-2 gap-x-40 gap-y-4">
                <InputComponent
                  label={
                    <span className="flex">
                      <span className="">Bank Account Name</span>
                      <span className="flex items-end text-red-500 pl-1 ">*</span>
                    </span>
                  }
                  name="bank_account_name"
                  value={formData.bank_account_name}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Name"
                  height="30px"
                  className="flex items-center "
                  labelClassName="flex items-center text-[#666] w-1/2"
                />
                <InputComponent
                  label={
                    <span className="flex">
                      <span className="">Account Number</span>
                      <span className="flex items-end text-red-500 pl-1 ">*</span>
                    </span>
                  }
                  name="bank_account_number"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Enter Account Number"
                  height="30px"
                  className="flex items-center "
                  labelClassName="flex items-center text-[#666] w-1/2"
                />
                <InputComponent
                  label="Routing Number"
                  name="routing_number"
                  value={formData.routing_number}
                  onChange={handleInputChange}
                  placeholder="Enter Routing Number"
                  height="30px"
                  className="flex items-center "
                  labelClassName="flex items-center text-[#666] w-1/2"
                />
                <InputComponent
                  label="Payment Terms"
                  name="payment_terms"
                  value={formData.payment_terms}
                  onChange={handleInputChange}
                  placeholder="Online"
                  height="30px"
                  className="flex items-center "
                  labelClassName="flex items-center text-[#666] w-1/2"
                />
                <InputComponent
                  label="Opening Balance"
                  name="opening_balance"
                  value={formData.opening_balance}
                  onChange={handleInputChange}
                  placeholder="00.00"
                  height="30px"
                  type="number"
                  className="flex items-center "
                  labelClassName="flex items-center text-[#666] w-1/2"
                />
              </div>
              <div className="flex justify-start gap-3 mt-8">
                <Button
                  variant="secondary"
                  onClick={handleCancelForm}
                  className="whitespace-nowrap px-4 bg-[white] border-solid hover:bg-[white] hover:border-[#004EFF] hover:text-[#004EFF]"
                >
                  CANCEL
                </Button>
                <Button className="bg-[#5489FF] hover:bg-[#004EFF]" onClick={handleSaveAndSubmit}>
                  {selectedEditIndex !== null ? "UPDATE" : "SAVE"}
                </Button>
              </div>
            </div>
          )}
          {financialData.length > 0 && (
            <div className="flex flex-col border p-4 border-[#D2DAE1] rounded-md h-fit overflow-y-auto thinscrollbar">
              {financialData.map((item, index) => {
                const isLastItem = index === financialData.length - 1;
                const showBorder = financialData.length > 1 && !isLastItem;
                return (
                  <div
                    key={index}
                    className={`w-full ${
                      isDisable === index.toString() && showAddForm ? "opacity-50 pointer-events-none bg-[#D2DAE1]" : ""
                    }`}
                  >
                    <div
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className={`relative px-4 py-3 transition-all duration-200 flex justify-between items-start h-full 
                        ${hoveredIndex === index ? "bg-[#F4F4F4] rounded-md" : ""}
                        ${showBorder ? "border-b-2 border-[#D2DAE1]" : ""}
                      `}
                    >
                      <div className="w-[60%] flex flex-col gap-3">
                        <div className="flex gap-10">
                          <div className="w-[40%] text-14 font-500 text-[#666]">Bank Account Name:</div>
                          <div className="w-[60%] text-14 font-400 text-[#666] truncate">{item.bank_account_name}</div>
                        </div>
                        <div className="flex gap-10">
                          <div className="w-[40%] text-14 font-500 text-[#666]">Bank Account Number:</div>
                          <div className="w-[60%] text-14 font-400 text-[#666] truncate">
                            {item.bank_account_number}
                          </div>
                        </div>
                        <div className="flex gap-10">
                          <div className="w-[40%] text-14 font-500 text-[#666]">Routing Number:</div>
                          <div className="w-[60%] text-14 font-400 text-[#666] truncate">{item.routing_number}</div>
                        </div>
                        <div className="flex gap-10">
                          <div className="w-[40%] text-14 font-500 text-[#666]">Payment:</div>
                          <div className="w-[60%] text-14 font-400 text-[#666] truncate">{item.payment_terms}</div>
                        </div>
                        <div className="flex gap-10">
                          <div className="w-[40%] text-14 font-500 text-[#666]">Opening Balance:</div>
                          <div className="w-[60%] text-14 font-400 text-[#666] truncate">
                            {parseFloat(item.opening_balance || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div className="w-[40%] h-full flex flex-col justify-between items-end ml-4">
                        <label
                          className={`flex items-center text-sm mt-1 ${
                            item.is_primary === 1 ? "text-[#2999BC]" : "text-[#666666]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={item.is_primary === 1}
                            onChange={() => handleDefaultChange(index)}
                            className={`mr-2 h-4 w-4 rounded border-gray-400 bg-white focus:ring-0 focus:outline-none ${
                              item.is_primary === 1 ? "text-[#2999BC] border-[#2999BC]" : "text-gray-500"
                            }`}
                            disabled={isDisable === index.toString() && showAddForm}
                          />
                          Default Bank
                        </label>
                        <div
                          className={`flex gap-4 transition-opacity duration-300 ${
                            hoveredIndex === index && !(isDisable === index.toString() && showAddForm)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        >
                          <button
                            onClick={() => handleEdit(index)}
                            disabled={isDisable === index.toString() && showAddForm}
                          >
                            <CircleEdit />
                          </button>
                          {false && (
                            <button
                              onClick={() => handleDelete(index)}
                              disabled={isDisable === index.toString() && showAddForm}
                            >
                              <CircleDelete />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialDetailsForm;
